/* eslint-disable react-hooks/exhaustive-deps */
import { FunctionComponent as F, useEffect, useState } from "react";
import { Box } from "@mui/material";
import loadingGif from '../../images/loading.gif';
import { ISpiel } from "@protoplan/types";
import { getSpiels, postNewRefVote } from "../../core/http/ref";
import { alphabeticalSort } from "../../utils/string";
import RefsTableHeader from "./RefsTableHeader";
import RefsTableBody from "./RefsTableBody";
import { handleToggleSortValues } from "../../utils/table";
import MaUTable from '@mui/material/Table';
import { SortDirection, SortFn } from "../../core/types/component";


export interface IPost
{
  topicId: number,
  postId: number,
  title: string,
  date: Date,
  url: string,
  platform: string,
  spiels: ISpiel[],
  mediaId: string | null,
  postLength: string,
  votes: number;
  voted: boolean;
  bookFormat?: string;
}

interface IRef
{
  refId: number;
  timestamp: string;
}

export interface IValue extends IPost
{
  startTime: string,
  nRefsInPost: number;
  refs: IRef[]
}


interface IProps
{
  topicId: number
}


const RefsTable: F<IProps> = (props: IProps) =>
{
  const headings = [ {
    Header: 'Title', accessor: 'title' }, {
    Header: 'Platform', accessor: 'platform' }, {
    Header: 'Date', accessor: 'date' }, {
    Header: 'Start time', accessor: 'startTime' }, {
    Header: 'Mentions', accessor: 'nRefsInPost' }, {
    Header: 'Votes', accessor: 'votes' }, {
    Header: '', accessor: 'url' } ];
  const [values, setValues] = useState<IValue[]>([]);

  const displayData = (postSpiels: IPost[]) =>
  {
    return postSpiels.map(p =>
    {
      const refs = p.spiels.map(s => s.refs).flat();

      return ({
        ...p,
        refs,
        nRefsInPost: refs.length,
        startTime: refs[0].timestamp });
    });
  };

  useEffect(() =>
  {
    setValues([]);
    getSpiels(props.topicId)
      .then(postSpiels =>
      {
        setValues(postSpiels ?
          [...displayData(postSpiels)].sort((a, b) => b.refs.length - a.refs.length) :
          []);
      });
  }, [props.topicId]);


  const [mentionsSort, setMentionsSort] = useState<SortDirection | null>(SortDirection.descending);
  const [titleSort, setTitleSort] = useState<SortDirection | null>(null);
  const [platformSort, setPlatformSort] = useState<SortDirection | null>(null);
  const [dateSort, setDateSort] = useState<SortDirection | null>(null);
  const [votesSort, setVotesSort] = useState<SortDirection | null>(null);
  const allSetSorts = [setMentionsSort, setTitleSort, setPlatformSort, setDateSort, setVotesSort];

  const handleToggleSort = (
    key: string,
    sort: SortDirection | null,
    setSort: (value: React.SetStateAction<SortDirection | null>) => void,
    sortFn?: SortFn) =>
    handleToggleSortValues<IValue>({
      values,
      setValues,
      key,
      sortDirection: sort,
      setSortDirection: setSort,
      unsetSortDirections: allSetSorts,
      sortFn });

  async function handleVote(refId: number)
  {
    const recordsCopy = [...values.map(r => ({ ...r}))];
    recordsCopy.forEach(r =>
    {
      if(r.spiels[0].refs[0].refId !== refId)
        return;

      r.votes = r.votes + 1 * (r.voted ? -1 : 1);
      r.voted = !r.voted;
    });
    setValues(recordsCopy);

    postNewRefVote(refId);
  }



  return (
    values ?
      (<MaUTable size='small'>
        <RefsTableHeader
          key={props.topicId}
          headings={headings}
          mentionsSort={mentionsSort}
          titleSort={titleSort}
          platformSort={platformSort}
          dateSort={dateSort}
          votesSort={votesSort}
          handleToggleMentionsSort={handleToggleSort('nRefsInPost', mentionsSort, setMentionsSort)}
          handleToggleTitleSort={handleToggleSort('title', titleSort, setTitleSort, alphabeticalSort)}
          handleTogglePlatformSort={handleToggleSort('platform', platformSort, setPlatformSort, alphabeticalSort)}
          handleToggleDateSort={handleToggleSort('date', dateSort, setDateSort)}
          handleToggleVotesSort={handleToggleSort('votes', votesSort, setVotesSort)} />
        <RefsTableBody topicId={props.topicId} headings={headings} records={values} handleVote={handleVote} />
      </MaUTable>) :
      (<Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <img src={loadingGif} width='100px'/>
      </Box>));
};

export default RefsTable;
